import { useReactFlow } from '@xyflow/react';
import { useCallback, useState, useEffect } from 'react';

export interface StrategyNodeData {
  label?: string;
  isLocked?: boolean;
  isMinimized?: boolean;
  minimizeLevel?: 'normal' | 'medium' | 'minimum'; // ✅ ADD: minimizeLevel support
  assignedEmails?: string[];
  currentSituationQuality?: string;
  currentSituationDetails?: string;
  targetSituationQuality?: string;
  targetSituationDetails?: string;
  middleStepsQuality?: string;
  middleStepsDetails?: string;
  minifiedInfo?: Record<string, any>; // ✅ ADD: minifiedInfo support
  documentCommand?: {
    'sysagent-cmd'?: string;
    'sysagent-response'?: string;
    processing?: boolean;
  };
}

export const useStrategyState = (
  id: string,
  data: StrategyNodeData,
  updateNodeData: (nodeId: string, data: Partial<StrategyNodeData>) => void
) => {
  const isLocked = data.isLocked || false;
  const isMinimized = data.isMinimized || false;
  const minimizeLevel = data.minimizeLevel || 'normal'; // ✅ ADD: get minimizeLevel from data
  const assignedEmails = data.assignedEmails || [];

  // ✅ ADD: Local state for setIsMinimized function
  const [localIsMinimized, setLocalIsMinimized] = useState(isMinimized);

  // ✅ ADD: Sync local state with data changes
  useEffect(() => {
    setLocalIsMinimized(data.isMinimized || false);
  }, [data.isMinimized]);

  const handleLock = useCallback(() => {
    syncNodeData({ isLocked: !isLocked });
  }, [isLocked]);

  const handleMinimize = useCallback(() => {
    const newMinimized = !isMinimized;
    setLocalIsMinimized(newMinimized);
    syncNodeData({ 
      isMinimized: newMinimized,
      minimizeLevel: newMinimized ? 'medium' : 'normal'
    });
  }, [isMinimized]);

  const syncNodeData = useCallback(
    (newData: Partial<StrategyNodeData>) => {
      updateNodeData(id, newData);
    },
    [id, updateNodeData]
  );

  return {
    isLocked,
    isMinimized,
    minimizeLevel, // ✅ ADD: return minimizeLevel
    setIsMinimized: setLocalIsMinimized, // ✅ ADD: return setIsMinimized function
    assignedEmails,
    handleLock,
    handleMinimize,
    syncNodeData,
  };
};

export type { StrategyNodeData };